import { Stack, TextField } from '@mui/material'
import type { ChangeEvent, FocusEvent, KeyboardEvent } from 'react'
import { useEffect, useRef } from 'react'

import CustomTypography from './CustomTypography'

interface DataGridTextFieldProps {
  acceptsOnlyPositiveInteger?: boolean
  disabled?: boolean
  readonlyField?: boolean
  selectValueOnFocus?: boolean
  value: string
  onChange?: (value: string) => void
  onBlur?: (value: string) => void
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
}

const DataGridTextField = ({
  acceptsOnlyPositiveInteger = false,
  selectValueOnFocus,
  value,
  disabled,
  onChange,
  onBlur,
  onKeyDown,
}: Omit<DataGridTextFieldProps, 'readonlyField'>) => {
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    inputRef.current?.focus()

    if (selectValueOnFocus) {
      inputRef.current?.select()
    }
  }, [])

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value

    if (acceptsOnlyPositiveInteger) {
      const integerRegex = /^\d*$/
      if (!integerRegex.test(newValue)) return
    }

    onChange?.(newValue)
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    onBlur?.(event.target.value)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event)
  }

  return (
    <TextField
      fullWidth
      color="primary"
      disabled={disabled}
      inputRef={inputRef}
      slotProps={{
        input: {
          sx: { fontSize: '0.875rem' },
        },
      }}
      type="text"
      value={value}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
    />
  )
}

/**
 * This component is useful when you want to use a TextField as a cell editor in a DataGrid.
 *
 * If you need an editable cell, you need to include the renderCell as well as the renderEditCell props of the DataGrid.
 *
 * For example:
 *
 * renderCell: (params: GridRenderCellParams<PtuVolumes>) => (
 *   <DataGridEditableTextField readonlyField value={params.formattedValue} />
 * ),
 * renderEditCell: (params: GridRenderEditCellParams<PtuVolumes>) => (
 *   <DataGridEditableTextField value={params.value} onChange={onAcceptedChange(params)} />
 * ),
 */
const DataGridEditableTextField = ({
  value,
  acceptsOnlyPositiveInteger,
  readonlyField,
  disabled,
  selectValueOnFocus,
  onChange,
  onBlur,
  onKeyDown,
}: DataGridTextFieldProps) => {
  if (readonlyField) {
    return (
      <Stack
        aria-disabled={disabled ? true : undefined}
        sx={{
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          height: '100%',
        }}
      >
        <CustomTypography
          sx={{
            padding: '9.5px 14px',
            borderRadius: '4px',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            width: '100%',
            backgroundColor: disabled ? '#f5f5f5' : 'white',
            color: disabled ? 'text.disabled' : 'inherit',
            cursor: disabled ? 'not-allowed' : 'default',
          }}
          tabIndex={0}
          variant="body2"
        >
          {value}
        </CustomTypography>
      </Stack>
    )
  }

  return (
    <DataGridTextField
      acceptsOnlyPositiveInteger={acceptsOnlyPositiveInteger}
      disabled={disabled}
      selectValueOnFocus={selectValueOnFocus}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
      onKeyDown={onKeyDown}
    />
  )
}

export default DataGridEditableTextField
